
import { useState, useEffect } from 'react';

export default function Hero() {
  const [scrolled, setScrolled] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    setLoaded(true);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section className="relative min-h-screen flex flex-col overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/524f30ea1da6748932dd2a8918a6fdff.jpg"
          alt="Digital infrastructure background"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950/80 via-slate-950/70 to-slate-950/95" />
      </div>

      {/* Floating Orbs */}
      <div className="absolute top-1/4 left-1/6 w-96 h-96 bg-teal-500/5 rounded-full blur-3xl animate-float" />
      <div className="absolute bottom-1/4 right-1/6 w-80 h-80 bg-teal-400/5 rounded-full blur-3xl animate-float-delayed" />

      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? 'glass shadow-lg shadow-slate-950/50' : ''}`}>
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer">
            <img
              src="https://static.readdy.ai/image/0fc33fa68f46f743570ad8ba1c3172ea/982a164fa40d3ba73c580c5f5b46fc70.png"
              alt="Palemia AI"
              className="h-9 w-9 object-contain"
            />
            <span className="text-white font-semibold text-lg tracking-tight">Palemia AI</span>
          </div>
          <div className="hidden md:flex items-center gap-10">
            <a href="#industries" className="text-gray-300 hover:text-white transition-colors duration-300 text-sm font-medium cursor-pointer whitespace-nowrap">Industries</a>
            <a href="#framework" className="text-gray-300 hover:text-white transition-colors duration-300 text-sm font-medium cursor-pointer whitespace-nowrap">Framework</a>
            <a href="#results" className="text-gray-300 hover:text-white transition-colors duration-300 text-sm font-medium cursor-pointer whitespace-nowrap">Results</a>
            <a href="#contact" className="px-5 py-2 bg-teal-500/10 border border-teal-500/30 text-teal-400 hover:bg-teal-500/20 transition-all duration-300 text-sm font-medium cursor-pointer whitespace-nowrap rounded-lg">Contact</a>
          </div>
        </div>
      </nav>

      {/* Hero Content */}
      <div className="relative z-10 flex-1 flex items-center justify-center px-6 pt-32 pb-24">
        <div className="max-w-5xl mx-auto text-center w-full">
          {/* Badge */}
          <div
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border border-teal-500/20 bg-teal-500/5 mb-10 transition-all duration-700 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          >
            <span className="w-2 h-2 rounded-full bg-teal-400 animate-pulse-glow" />
            <span className="text-teal-400 text-xs font-medium tracking-wider uppercase">AI Infrastructure for Service Industries</span>
          </div>

          {/* Headline */}
          <h1
            className={`text-6xl md:text-7xl lg:text-8xl font-bold text-white mb-8 tracking-tight leading-[1.05] transition-all duration-700 delay-100 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            Structured Intelligence.<br />
            <span className="bg-gradient-to-r from-teal-400 to-teal-300 bg-clip-text text-transparent">Scalable Growth.</span>
          </h1>

          {/* Subtext */}
          <p
            className={`text-lg md:text-xl text-gray-400 mb-14 max-w-3xl mx-auto leading-relaxed font-light transition-all duration-700 delay-200 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            Palemia AI builds advanced AI infrastructure for service-driven industries — from law and lending to healthcare, real estate, and core trades.
          </p>

          {/* CTA Buttons */}
          <div
            className={`flex flex-col sm:flex-row items-center justify-center gap-4 transition-all duration-700 delay-300 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <a
              href="#contact"
              className="px-8 py-4 bg-teal-500 text-slate-950 font-semibold text-sm hover:bg-teal-400 transition-all duration-300 rounded-lg cursor-pointer whitespace-nowrap hover:shadow-lg hover:shadow-teal-500/20 hover:-translate-y-0.5"
            >
              Schedule a Strategy Call
            </a>
            <a
              href="#framework"
              className="px-8 py-4 border border-gray-600 text-gray-300 font-medium text-sm hover:border-teal-500/50 hover:text-teal-400 transition-all duration-300 rounded-lg cursor-pointer whitespace-nowrap hover:-translate-y-0.5"
            >
              Explore Our Framework
            </a>
          </div>

          {/* Scroll Indicator */}
          <div className={`mt-20 flex flex-col items-center gap-2 transition-all duration-700 delay-500 ${loaded ? 'opacity-100' : 'opacity-0'}`}>
            <span className="text-gray-500 text-xs tracking-widest uppercase">Scroll</span>
            <div className="w-px h-8 bg-gradient-to-b from-gray-500 to-transparent" />
          </div>
        </div>
      </div>
    </section>
  );
}
